import React from "react";
import Main from "layouts/Main";
import { addDocument, modifyDataset, removeDatasetFile } from "services/api";
import { Box, Button, Checkbox, Divider, IconButton, InputAdornment, LinearProgress, List, ListItem, ListItemButton, ListItemText, TextField, Typography } from "@mui/material";
import FileUploader from "components/FileUploader";
import { AddCircleOutline, Close, Done, Error, WarningRounded } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { DatasetFile, isDatasetFile } from "types/dataset";
import { useTranslation } from "react-i18next";
import Loading from "components/Loading";
import { useConfirmDialog } from "components/ConfirmDialog";
import Container from "components/Container";

type Progress = "none" | "loading" | "success" | "error";
type UploadFile = File | DatasetFile | string;

const AddDocument = () => {
	const { id } = useParams();
	const { t } = useTranslation(["common"]);
	const confirmDialog = useConfirmDialog();

	const [datasetName, setDatasetName] = React.useState("");

	const [urlLink, setUrlLink] = React.useState("");
	const [urlError, setUrlError] = React.useState("");

	const [files, setFiles] = React.useState<Array<UploadFile>>([]);
	const [progresses, setProgresses] = React.useState<Array<Progress>>([]);
	const [errorMessage, setErrorMessage] = React.useState<Array<string>>([]);

	const [isProgressing, setIsProgressing] = React.useState(false);
	const [isLoading, setLoading] = React.useState(false);

	const uploadable = React.useMemo(() => {
		return files.filter((file) => !isDatasetFile(file)).length > 0;
	}, [files]);
	const [dialogTotal, setDialogTotal] = React.useState(0);
	const [dialogDone, setDialogDone] = React.useState(0);
	const handleUrlAdd = (url: string) => {
		try {
			new URL(url);
			handleAdd([url]);
		} catch (e) {
			setUrlError(t("invaildUrl", { ns: "validation" }));
		}
	};
	const handleAdd = (files: Array<File | string>) => {
		setFiles((prev) => [...prev, ...files]);
	};

	const getTitle = (file: UploadFile) => {
		if (typeof file === "string") return file;
		if (file instanceof File) return file.name;
		return file.title;
	};
	const hasErrorBefore = progresses.some((p) => p === "error");
	const filesToUploadCount = files.filter(
		(file, i) =>
			!isDatasetFile(file) &&
			progresses[i] !== "success" // not uploaded yet
	).length;
	const handleUpload = () => {
		console.log("handle upload now",isProgressing);
		if (isProgressing) return;
		const innerHandleUpload = async () => {
		// figure out which indexes we want to upload
		const indexes = files
			.map((file, i) => ({ file, i }))
			.filter(
				({ file, i }) =>
					!isDatasetFile(file) && progresses[i] !== "success"
			)
			.map(({ i }) => i);

		if (indexes.length === 0) return;

		const total = indexes.length;
		const hasErrorBefore = progresses.some((p) => p === "error");

		const title = t("uploadDocuments");
		const message = !hasErrorBefore
			? t("uploadConfirmMessage", { count: total })  // "We are going to upload X file(s)"
			: t("uploadRetryMessage", { count: total });   // "Retry uploading X file(s)?"

		confirmDialog.openDialog(
			title,
			message,
			async () => {
				setIsProgressing(true);
				setDialogTotal(total);
				setDialogDone(0);

				const maxFileSize = 100 * 1024 * 1024;

				// mark selected indexes as loading
				setProgresses((prev) => {
					const next = [...prev];
					for (const idx of indexes) {
						if (next[idx] !== "success") next[idx] = "loading";
					}
					return next;
				});

				const chunkSize = 3;
				var errorCount = 0;
				for (let start = 0; start < indexes.length; start += chunkSize) {
					const chunk = indexes.slice(start, start + chunkSize);

					await Promise.all(
						chunk.map(async (idx) => {
							const file = files[idx];

							// size check
							if (file instanceof File && file.size > maxFileSize) {
								setErrorMessage((prev) => {
									const next = [...prev];
									next[idx] = "exceededFileSize";
									return next;
								});
								setProgresses((prev) => {
									const next = [...prev];
									next[idx] = "error";
									return next;
								});
								setDialogDone((prev) => prev + 1);
								return;
							}

							try {
								const resp = await addDocument(id, file as File | string);
								if (resp && typeof resp === "object" && "error" in resp && resp.error) {
									// Normalize to Error so catch(err) works consistently
									throw new  (globalThis as any).Error(resp.error);
								  }
								setFiles((prev) => {
									const next = [...prev];
									next[idx] = { fileId: resp.fileId, title: getTitle(file) };
									return next;
								});
								setProgresses((prev) => {
									const next = [...prev];
									next[idx] = "success";
									return next;
								});
							} catch (err: any) {
								errorCount += 1;
								setErrorMessage((prev) => {
									const next = [...prev];
									next[idx] = "Connection Timeout" //err?.message || "uploadFailed";
									return next;
								});
								setProgresses((prev) => {
									const next = [...prev];
									next[idx] = "error";
									return next;
								});
							} finally {
								setDialogDone((prev) => prev + 1);
							}
						})
					);
				}
				
				setIsProgressing(false);
				
				
			}
		);}
		innerHandleUpload();
	};



	const removeFile = (index: number) => {
		setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
		setProgresses((prevProgresses) => prevProgresses.filter((_, i) => i !== index));
		setErrorMessage((prev) => prev.filter((_, i) => i !== index));
	};


	const onRemove = (index: number) => {
		// remove file from dataset with onBatchRemove
		handleBatchRemove(index);
	};
	const handleRemove = (index: number) => {
		if (progresses[index] === "success") {
			confirmDialog.openDialog(t("deleteDocument"), t("deleteDocumentMessage"), () => onRemove(index));
		} else {
			removeFile(index);
		}
	};

	React.useEffect(() => {
		if (id) {
			setLoading(true);
			modifyDataset(id, "getInfo", {}).then((resp) => {
				if (!resp || resp?.error) return;
				setDatasetName(resp.name);
			});
			modifyDataset(id, "getFileList", {})
				.then((resp) => {
					if (!resp || resp?.error) return;
					setFiles(resp.files);
					setProgresses(new Array(resp.files.length).fill("success"));
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, []);
	const [selected, setSelected] = React.useState<number[]>([]);

	const toggleSelect = (index: number) => {
		setSelected(prev =>
			prev.includes(index)
				? prev.filter(i => i !== index)
				: [...prev, index]
		);
	};

	const handleBatchRemove = (index?: number) => {

		// assuming items is in parent; here just pass selected
		const indexes =
			typeof index === "number"
				? [index]
				: selected.slice().sort((a, b) => b - a);
		console.log("handleBatchRemove", indexes, selected);
		const total = indexes.length;

		confirmDialog.openDialog(
			t("deleteDocument"),
			t("deleteDocumentMessage"),
			async () => {
				// set total & reset done via dialog provider setters
				// you can expose setters via a hook or pass via payload;
				// simplest: lift progress state into this component instead of provider

				setDialogTotal(total);
				setDialogDone(0);

				for (const index of indexes) {
					console.log("Removing index", index);
					if (progresses[index] === "success") {
						const resp = await removeDatasetFile(
							id,
							(files[index] as DatasetFile).fileId
						);
						if (resp.info) {
							removeFile(index);
						}
					} else {
						removeFile(index);
					}
					setDialogDone(prev => prev + 1);
				}
				if (typeof index === "undefined") {
					setSelected([]);
				}
			}
		);
	};
	return (
		<Main title={t("addDocument")}>
			{isLoading ? (
				<Loading />
			) : (
				<Container mt={2}>
					<Typography
						variant="h4"
						component={"h2"}>
						{datasetName}
					</Typography>
					<Box
						width={1}
						mt={1}
						mb={3}>
						<Divider />
					</Box>
					<FileUploader
						accept=".csv, .json, .xml, .pdf, .ppt, .pptx, .doc, .docx, .xls, .xlsx, .html, .md, .txt"
						multiple
						handleUpload={handleAdd}
					/>
					<Divider sx={{ my: 1 }}>{t("or")}</Divider>
					<TextField
						label={t("url")}
						fullWidth
						variant="outlined"
						placeholder="https://xxxxxxxxxx"
						value={urlLink}
						error={Boolean(urlError)}
						helperText={urlError}
						onChange={(e) => setUrlLink(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								handleUrlAdd(urlLink);
								setUrlLink("");
							}
						}}
						slotProps={{
							input: {
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => {
												handleUrlAdd(urlLink);
												setUrlLink("");
											}}
											size="small"
											sx={{ color: "text.secondary" }}>
											<AddCircleOutline />
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
					<Divider sx={{ my: 2 }} />
					<Box width="100%" mt={2} mb={1} display="flex" justifyContent="flex-end">
						<Button
							variant="outlined"
							color="error"
							size="small"
							disabled={selected.length === 0}
							onClick={() => { handleBatchRemove() }}
						>
							{t("Remove selected")} ({selected.length})
						</Button>
					</Box>
					<List dense>
						{files.map((file, i) => {
							
							const name =
								file instanceof File
									? file.name
									: typeof file === "string"
										? file
										: file.title;
							const isSelected = selected.includes(i);

							return (
								<ListItem
									key={i}
									disablePadding
								// secondaryAction={
								// <IconButton
								// 	size="small"
								// 	disabled={status === "loading"}
								// 	onClick={() => handleRemove(i)}
								// 	sx={{ color: "text.secondary" }}
								// >
								// 	<Close />
								// </IconButton>
								// }
								>
									<ListItemButton dense onClick={() => toggleSelect(i)}>
										<Checkbox
											edge="start"
											tabIndex={-1}
											disableRipple
											checked={isSelected}
										/>
										<ListItemText>
											<Box sx={{ display: "flex", alignItems: "center" }}>
												{progresses[i] === "success" && <Done color="success" />}
												{progresses[i] === "error" && <Error color="error" />}
												<Typography
													noWrap
													color={
														progresses[i] === "success"
															? "success"
															: progresses[i] === "error"
																? "error"
																: "inherit"
													}
												>
													{name}
												</Typography>
											</Box>
											{progresses[i] === "loading" && <LinearProgress />}
											{progresses[i] === "error" && errorMessage[i] && (
												<Typography color="error">{t(errorMessage[i])}</Typography>
											)}
										</ListItemText>
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>

					{isProgressing && (
						<Typography
							color="warning"
							sx={{ mb: 1, display: "flex", alignItems: "center" }}>
							<WarningRounded sx={{ height: 16, weight: 16 }} />
							{t("uploadRemindMessage")}
						</Typography>
					)}

					<Box
						display={uploadable ? "flex" : "none"}
						alignItems="center"
						gap={1}>
						<Button
							variant="contained"
							color="error"
							disabled={isProgressing}
							onClick={() =>
								setFiles(
									files.filter((_, i) => {
										return progresses[i] === "success";
									})
								)
							}>
							{t("clear")}
						</Button>
						<Button
							variant="contained"
							disabled={isProgressing}
							onClick={handleUpload}>
							{t("upload")}
						</Button>
					</Box>
				</Container>
			)}
		</Main>
	);
};

export default AddDocument;
